/**
 * CriticalFlagConfirmDialog
 *
 * Confirmation dialog for actions on flags marked `isCritical`. The operator
 * must type the flag key exactly (case-sensitive) before the confirm button
 * unlocks. Typed state resets every time the dialog opens, so re-opening
 * always starts blank.
 */

import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

export interface CriticalFlagConfirmDialogProps {
  open: boolean;
  flagKey: string;
  action: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function CriticalFlagConfirmDialog({
  open,
  flagKey,
  action,
  onConfirm,
  onCancel,
}: CriticalFlagConfirmDialogProps) {
  const [typedValue, setTypedValue] = useState('');

  useEffect(() => {
    if (!open) {
      setTypedValue('');
    }
  }, [open]);

  const handleCancel = () => {
    setTypedValue('');
    onCancel();
  };

  const handleConfirm = () => {
    if (typedValue !== flagKey) {
      return;
    }
    setTypedValue('');
    onConfirm();
  };

  const matches = typedValue === flagKey;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="critical-flag-confirm-title"
    >
      <DialogTitle id="critical-flag-confirm-title">
        Confirm critical flag change
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="warning">
            This is a critical feature flag. Mistakes can break tenants. Read
            the action below carefully before confirming.
          </Alert>
          <DialogContentText>
            You are about to <strong>{action}</strong> for flag{' '}
            <Typography
              component="span"
              sx={{ fontFamily: 'monospace', fontWeight: 600 }}
            >
              {flagKey}
            </Typography>
            .
          </DialogContentText>
          <DialogContentText>
            To confirm, type the flag key exactly as shown:
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Flag key"
            value={typedValue}
            onChange={(e) => setTypedValue(e.target.value)}
            inputProps={{
              'aria-label': 'Type flag key to confirm',
              spellCheck: false,
              autoCapitalize: 'off',
              autoCorrect: 'off',
            }}
            placeholder={flagKey}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          color="warning"
          variant="contained"
          disabled={!matches}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
